import React, { useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatchedReducer } from 'sb-hooks'
import { injectIntl, intlMessageShape } from 'sb-react-intl'

import UnderlineInputView from './UnderlineInputView/UnderlineInputView'
import StandardInputView from './StandardInputView/StandardInputView'


const fieldReducer = {
  initialState: {
    value: '',
    error: '',
    isValidating: false,
  },
  update: (state, values) => ({ ...state, ...values }),
  setValue: (state, value) => ({ ...state, value }),
  setValidationStatus: (state, status) => ({ ...state, isValidating: status }),
}


const useReducer = (field) => {
  const modifyInitialState = ({ value, error, ...rest }) => ({
    value: field.value || value,
    error: field.error || error,
    ...rest,
  })

  return useDispatchedReducer(fieldReducer, modifyInitialState)
}

const useFieldSubscribers = ({ ref, field, actions }) => {
  useEffect(() => {
    const handleFieldStartValidate = () => {
      actions.setValidationStatus(true)
    }

    const handleFieldValidate = (error) => {
      actions.update({
        isValidating: false,
        error,
      })
    }

    const handleChange = async (value) => {
      actions.setValue(value)

      // prevent field from validation until user blurred it once
      if (field.isValidated && field.validateAfterChange) {
        await field.debounceValidate()
      }
    }

    const handleUnset = () => {
      actions.setValue(field.value)
    }

    field.on('start validate', handleFieldStartValidate)
    field.on('validate', handleFieldValidate)
    field.on('change', handleChange)
    field.on('unset', handleUnset)

    return () => {
      field.off('start validate', handleFieldStartValidate)
      field.off('validate', handleFieldValidate)
      field.off('change', handleChange)
      field.off('unset', handleUnset)
    }
  }, [])
}

const useHandleChange = ({ field, pattern, mask, maskReplace, onChange }) =>
  useCallback((event) => {
    let { value } = event.target

    if (mask && value) {
      if (maskReplace) {
        value = value.replace(maskReplace, '')
      }
      // TODO looks weird, remove in weeks if there will no bugs :)
      // else if (maskReplace !== null && maskReplace !== false) {
      //   value = value.replace(/[^0-9]+/g, '')
      // }
    }
    else if (pattern && value) {
      value = value.replace(new RegExp(`[^${pattern}]+`, 'g'), '')
    }

    field.set(value)

    if (typeof onChange === 'function') {
      onChange(value)
    }
  }, [ pattern, mask, maskReplace, onChange ])

const useHandleBlur = ({ field, onBlur }) =>
  useCallback(async (event) => {
    await field.debounceValidate()

    // // prevent input from blurring if there is no async validation
    // if (!field.hasAsyncValidators) {
    //   event.preventDefault()
    // }
    //
    // const error = await field.debounceValidate()
    //
    // // blur input if there is no error
    // if (!field.hasAsyncValidators && !error) {
    //   field.node.blur()
    // }

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }, [ onBlur ])

const useFocusOnInit = ({ ref, isEnabled }) =>
  useEffect(() => {
    if (isEnabled) {
      ref.current.focus()
    }
  }, [])

const useFieldRef = ({ field }) => {
  const ref = useRef()

  useEffect(() => {
    field.setRef(ref.current)

    return () => {
      field.unsetRef()
    }
  }, [ ref.current ])

  return ref
}

const Field = (props) => {
  const {
    className,
    inputRef, field, type, label, placeholder, pattern, rows, autosize,
    icon, iconColor,
    mask, maskChar, maskGuide, maskReplace,
    errored, loading, readOnly, focusOnInit, withBorder, withEraser, standardView, semiboldPlaceholder,
    beforeNode, afterNode,
    onSubmit, onIconClick, onChange, onFocus, onBlur, onErase, onTouchStart, onBeforeNodeClick, onAfterNodeClick,
    dataTestId, id,
    blackOnFocus, // test for /subscribe-prototype
    iconButtonProps,
    autoComplete,
    intl,
  } = props

  const [ state, actions ] = useReducer(field)
  const ref = useFieldRef({ field })

  useFieldSubscribers({ ref, field, actions })
  useFocusOnInit({ ref, isEnabled: focusOnInit })

  const handleChange  = useHandleChange({ field, pattern, mask, maskReplace, onChange })
  const handleBlur    = useHandleBlur({ field, onBlur })
  const handleErase   = useCallback(() => {
    field.unset()

    if (typeof onErase === 'function') {
      onErase()
    }
  }, [ field, onErase ])

  useEffect(() => {
    // update value if user entered something to input rendered by SSR when client react is not initialized yet
    field.set(ref.current.value)
  }, [])

  const resolveRef = (node) => {
    ref.current = node

    if (inputRef) {
      inputRef.current = node
    }
  }

  const nodeProps = {
    id,
    className,
    ref: resolveRef,
    type,
    label,
    value: state.value,
    error: state.error,
    pattern,
    mask,
    maskChar,
    maskGuide,
    errored,
    loading,
    readOnly,
    disabled: readOnly,
    withEraser,
    beforeNode,
    afterNode,
    blackOnFocus, // test for /subscribe-prototype
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus,
    onErase: handleErase,
    onTouchStart,
    onBeforeNodeClick,
    onAfterNodeClick,
    dataTestId,
    iconButtonProps,
    icon,
    iconColor,
    onIconClick,
    autoComplete,
  }

  if (standardView) {
    nodeProps.placeholder = placeholder
    nodeProps.withBorder = withBorder
    nodeProps.onSubmit = onSubmit
    nodeProps.rows = rows
    nodeProps.autosize = autosize
    nodeProps.semiboldPlaceholder = semiboldPlaceholder
  }

  nodeProps.eraserLabel = label ? `Clear ${intl.message(label)}` : 'Clear input'

  return React.createElement(standardView ? StandardInputView : UnderlineInputView, nodeProps)
}

Field.propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  field: PropTypes.object.isRequired,
  className: PropTypes.string,
  type: PropTypes.string,
  rows: PropTypes.number,
  autosize: PropTypes.bool,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  pattern: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  maskChar: PropTypes.string,
  maskGuide: PropTypes.bool,
  maskReplace: PropTypes.bool,
  errored: PropTypes.bool,
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  focusOnInit: PropTypes.bool,
  withBorder: PropTypes.bool,
  withEraser: PropTypes.bool,
  semiboldPlaceholder: PropTypes.bool,
  beforeNode: PropTypes.node,
  afterNode: PropTypes.node,
  onSubmit: PropTypes.func,
  onIconClick: PropTypes.func,
  onBeforeNodeClick: PropTypes.func,
  onAfterNodeClick: PropTypes.func,
  onErase: PropTypes.func,
  standardView: PropTypes.bool,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  iconButtonProps: PropTypes.object,
}

Field.defaultProps = {
  // 'nope' was a hack to remove autoComplete, coz 'off' doesn't work
  // bug: https://bugs.chromium.org/p/chromium/issues/detail?id=914451
  // workarond: https://goo.gl/PeZ856
  // 'nope' is ignored? https://github.com/Rendez/downshift/commit/bf876844925545bab3cb32d6ff66c1beeb056199
  autoComplete: 'off',
}


export default injectIntl(Field)
